import { Theme, alpha, lighten } from "@mui/material/styles";

// The S4 table: three pinned columns (สาขา · วันขาย · สาย), a two-row sticky header, a sticky
// รวม row. Pinned and header cells are opaque so what scrolls under them stays hidden.

export const TABLE_MAX_HEIGHT = 560;
export const HEAD_ROW_HEIGHT = 36;
const BRANCH_WIDTH = 168;
const DAYS_WIDTH = 64;
const ROUTES_WIDTH = 56;
const HEADER_TINT = 0.88;
const ADJUSTED_OPACITY = 0.12;
const TOTAL_COLUMN_OPACITY = 0.04;

export type PinnedColumn = "BRANCH" | "DAYS" | "ROUTES";

const PINNED: Record<PinnedColumn, { left: number; width: number }> = {
  BRANCH: { left: 0, width: BRANCH_WIDTH },
  DAYS: { left: BRANCH_WIDTH, width: DAYS_WIDTH },
  ROUTES: { left: BRANCH_WIDTH + DAYS_WIDTH, width: ROUTES_WIDTH },
};

/** A pinned column; `layer` lifts header / footer corners above both scroll directions. */
export const pinnedSx = (column: PinnedColumn, layer = 1) => ({
  position: "sticky",
  left: PINNED[column].left,
  minWidth: PINNED[column].width,
  zIndex: layer,
  bgcolor: "inherit",
  ...(column === "ROUTES" ? { borderRight: 1, borderRightColor: "divider" } : {}),
});

/**
 * A pinned header corner. It paints the header background itself: the header row is
 * transparent, so inheriting would let the groups that scroll under it show through.
 */
export const pinnedHeadSx = (column: PinnedColumn, layer: number) => ({
  ...pinnedSx(column, layer),
  bgcolor: (theme: Theme) => theme.palette.background.default,
});

/** A header cell of row 1 or 2 (row 2 sits under row 1). */
export const headCellSx = (headerRow: 1 | 2) => ({
  position: "sticky",
  top: headerRow === 1 ? 0 : HEAD_ROW_HEIGHT,
  zIndex: 3,
  height: HEAD_ROW_HEIGHT,
  py: 0,
  bgcolor: (theme: Theme) => theme.palette.background.default,
  fontWeight: 600,
  textAlign: "right",
});

export const GROUP_SEPARATOR_SX = { borderLeft: 1, borderLeftColor: "divider" } as const;

/** จากรายวัน is the system's group; the estimate is the purple of an estimate everywhere. */
export const dailyHeadSx = {
  bgcolor: (theme: Theme) => lighten(theme.palette.success.main, HEADER_TINT),
};
export const estimateHeadSx = {
  bgcolor: (theme: Theme) => lighten(theme.palette.primary.main, HEADER_TINT),
  color: "primary.dark",
};

export const TOTAL_COLUMN_SX = {
  fontWeight: 600,
  bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, TOTAL_COLUMN_OPACITY),
} as const;

/** ปรับหรือแก้ทับแล้ว: amber, as the legend says. */
export const ADJUSTED_SX = {
  bgcolor: (theme: Theme) => alpha(theme.palette.warning.main, ADJUSTED_OPACITY),
} as const;

export const ESTIMATE_TEXT_SX = {
  fontStyle: "italic",
  fontWeight: 600,
  color: "primary.dark",
} as const;

export const CELL_SX = { px: 0.75, py: 0.25 } as const;
